import { useQueryClient } from "@tanstack/react-query";

import { JSX } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import type { Subscription } from "@/@types/subscriptions";
import { USER_KEYS, useChangeSubsAutoPayment } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

const MySubscriptionCard = ({
  id,
  title,
  description_list,
  icon,
  price,
  date_of_debiting,
  auto_payment,
  prices,
}: Subscription): JSX.Element => {
  const { t } = useTranslation();

  const lang = useLang();

  const { openModal } = useModalRouter();
  const { mutate: changeAutoPayment } = useChangeSubsAutoPayment();

  const queryClient = useQueryClient();

  const formattedDate =
    date_of_debiting &&
    new Date(date_of_debiting).toLocaleDateString("ru", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const handleAutoPayment = () => {
    if (auto_payment) {
      openModal(MODAL_ROUTES.CANCEL_SUBSCRIPTION, {
        subscription_id: id.toString(),
      });

      return;
    }

    changeAutoPayment(
      {
        subscription_id: id,
        auto_payment: true,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: USER_KEYS.userDetail(lang) });
          onSuccess(t("Subscription.restored"));
        },
        onError,
      },
    );
  };

  return (
    <div
      key={id}
      className={"subs-card flex h-full w-full flex-col justify-between gap-3 rounded-3xl bg-surface-container-low p-4"}
    >
      <div className={"space-y-3"}>
        <div className={"flex items-center"}>
          <div className={"h-14"}>
            {icon ? <img src={icon} alt="Start" className={"mr-4 size-14 object-contain"} /> : null}
          </div>
          <p className={cn("text-2xl  font-semibold")}>{title}</p>
        </div>
        <div>
          <p className={"text-2xl font-semibold text-[#AB65D4]"}>{t("General.som", { value: price / 100 })}</p>
          <p className={"text-grey-1"}>{t("Subscription.one-month-30")}</p>
        </div>
        <div
          dangerouslySetInnerHTML={{
            __html: description_list,
          }}
          className={"prose prose-invert text-on-primary"}
        />
      </div>
      <div className={"space-y-3"}>
        {date_of_debiting && (
          <p>
            <span className={"text-grey-1"}>{t("Subscription.next-write-off")}:</span>{" "}
            <span className={"text-orange-1"}>{formattedDate}</span>
          </p>
        )}
        {date_of_debiting && (
          <Button
            color={"white"}
            onClick={handleAutoPayment}
            aria-label={t("Subscription.cancel-subscription")}
            size={"medium"}
          >
            {auto_payment ? t("Subscription.cancel-subscription") : t("Subscription.restore")}
          </Button>
        )}
        {!date_of_debiting && (
          <Button
            size={"medium"}
            aria-label={t("Subscription.add-subscription")}
            onClick={() => {
              if (prices.length == 1) {
                openModal([MODAL_ROUTES.BUY_SUBSCRIPTION, MODAL_ROUTES.BUY_SUBSCRIPTION_METHODS], {
                  price_id: prices[0]?.id.toString(),
                  subscription_id: id.toString(),
                });
                return;
              }

              openModal([MODAL_ROUTES.BUY_SUBSCRIPTION, MODAL_ROUTES.BUY_SUBSCRIPTION_PRICES], {
                subscription_id: id.toString(),
              });
            }}
          >
            {t("Actions.buy")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default MySubscriptionCard;
